import { t } from "@lingui/macro";
import {
  CreateTasksFromTemplateGroupDocument,
  type CreateTasksFromTemplateGroupMutation,
  type CreateTasksFromTemplateGroupMutationVariables,
  TaskGroupTemplateDocument,
  type TaskGroupTemplateFragment,
  type TaskGroupTemplateQuery,
  type TaskGroupTemplateQueryVariables,
  TaskGroupTemplatesSimpleDocument,
  type TaskGroupTemplatesSimpleQuery,
  type TaskGroupTemplatesSimpleQueryVariables,
} from "@src/__generated__/graphql";
import type { IOption } from "@src/components/ui-kit";
import { FetchHelper } from "@src/helpers/apollo/fetch";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { UIStore } from "@src/stores/UIStore/UIStore";
import { required } from "@src/utils/forms/validators";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { FieldState } from "formstate";
import { compact } from "lodash";
import { action, computed, makeObservable, observable } from "mobx";
import type { OurWorkOverviewModel } from "../models";

type ProjectCreateTasksFromTemplateGroupDisclosureProps = {
  initialSelectedId: string;
  ourWorkOverview: OurWorkOverviewModel[];
  onSubmit: () => void;
};
export class ProjectCreateTasksFromTemplateGroupStore {
  disclosure =
    new DisclosureState<ProjectCreateTasksFromTemplateGroupDisclosureProps>({
      onOpen: (additionalData) => {
        if (!additionalData) return;
        this.ourWorkOverview = additionalData.ourWorkOverview;
        this.handleSelectBudgetItems([additionalData.initialSelectedId]);
        this.fetchTaskGroupTemplates();
      },
      onClose: () => {
        this.ourWorkOverview = [];
        this.taskTemplateGroups = [];
        this.groupTemplateTasks = [];
        this.taskGroupTemplateId.reset(undefined);
        this.handleSelectBudgetItems([]);
      },
    });
  groupsSimpleFetcher = new FetchHelper<
    TaskGroupTemplatesSimpleQuery,
    TaskGroupTemplatesSimpleQueryVariables
  >(TaskGroupTemplatesSimpleDocument);
  groupFetcher = new FetchHelper<
    TaskGroupTemplateQuery,
    TaskGroupTemplateQueryVariables
  >(TaskGroupTemplateDocument);
  mutator = new MutationHelper<
    CreateTasksFromTemplateGroupMutation,
    CreateTasksFromTemplateGroupMutationVariables
  >(CreateTasksFromTemplateGroupDocument);

  @observable
  selectedBudgetItemIds: string[] = [];
  @observable
  ourWorkOverview: OurWorkOverviewModel[] = [];
  @observable
  taskTemplateGroups: TaskGroupTemplateFragment[] = [];
  @observable.ref
  groupTemplateTasks: NonNullable<TaskGroupTemplateFragment["tasks"]> = [];

  taskGroupTemplateId = new FieldState<string | undefined>(
    undefined,
  ).validators(required);

  constructor(private UISTore: UIStore) {
    makeObservable(this);
  }

  @computed
  get budgetItemOptions(): IOption[] {
    return this.ourWorkOverview
      .flatMap((overview) => overview.budgetItems)
      .map((budgetItem) => ({
        value: budgetItem.id,
        label: budgetItem.title,
      }));
  }

  @computed
  get taskGroupOptions(): IOption[] {
    return this.taskTemplateGroups.map((group) => ({
      value: group.id,
      label: group.name,
    }));
  }

  @computed
  get showSelectAllButton() {
    return this.selectedBudgetItemIds.length !== this.budgetItemOptions.length;
  }

  @action.bound
  handleSelectBudgetItems(ids: string[]) {
    this.selectedBudgetItemIds = ids;
  }

  @action.bound
  handleSelectTaskGroupTemplate(id: string | undefined) {
    this.taskGroupTemplateId.onChange(id);
    this.fetchGroupTemplateTasks(id);
  }

  @action.bound
  async fetchTaskGroupTemplates() {
    const [data, error] = await this.groupsSimpleFetcher.fetch();
    if (error) return;
    this.taskTemplateGroups = compact(data.taskTemplateGroups);
  }

  @action.bound
  async fetchGroupTemplateTasks(groupTemplateId: string | undefined) {
    this.groupTemplateTasks = [];
    if (!groupTemplateId) return;

    const [data, error] = await this.groupFetcher.fetch({
      id: groupTemplateId,
    });

    if (error) return;
    this.groupTemplateTasks = data.taskTemplateGroup?.tasks ?? [];
  }

  @action.bound
  async handleConfirm() {
    const { hasError } = await this.taskGroupTemplateId.validate();

    if (hasError) return;
    if (!this.taskGroupTemplateId.$) return;

    const [_, error] = await this.mutator.mutate({
      ourWorkBudgetItemIds: this.selectedBudgetItemIds,
      taskTemplateGroupId: this.taskGroupTemplateId.$,
    });

    if (error) return;

    this.disclosure.additionalData?.onSubmit();
    this.disclosure.onClose();
    this.UISTore.toast({
      title: t`Tasks added successfully.`,
      status: "success",
    });
  }

  @action.bound
  handleSelectAllBudgetItems() {
    for (const { value } of this.budgetItemOptions) {
      if (this.selectedBudgetItemIds.includes(value)) continue;
      this.selectedBudgetItemIds.push(value);
    }
  }
}
